/**
 * Nango Connect (web): opens the `@nangohq/frontend` Connect UI widget with a
 * short-lived session token from `POST /api/connectors/session`.
 *
 * The package is loaded lazily so the bundle never hard-fails if it isn't
 * installed yet; the typed shape is declared locally (we don't depend on the
 * package's own types). On `connect`, Nango posts the connection id to our
 * `/webhooks/nango/auth` endpoint, so the connection is persisted server-side
 * regardless: `onResult` returns the connection id (when present) so the caller
 * can also call `/api/connectors/:slug/connect` to activate it immediately.
 */

/** Minimal local shape of the `@nangohq/frontend` Connect UI surface. */
interface NangoConnectEvent {
  type: string;
  payload?: Record<string, unknown>;
}

interface NangoConnectUI {
  setSessionToken(token: string): void;
  close?: () => void;
}

interface NangoInstance {
  openConnectUI(options: {
    onEvent?: (event: NangoConnectEvent) => void;
    baseURL?: string;
  }): NangoConnectUI;
}

type NangoCtor = new (config?: Record<string, unknown>) => NangoInstance;

export interface OpenNangoConnectParams {
  sessionToken: string;
  /** Fired once the user finishes the flow (connect): with the connection id when available. */
  onResult: (result: { connectionId?: string }) => void;
  /** Fired when the user closes the widget without connecting. */
  onClose?: () => void;
}

export async function openNangoConnect({
  sessionToken,
  onResult,
  onClose,
}: OpenNangoConnectParams): Promise<void> {
  const mod = (await import('@nangohq/frontend')) as unknown as {
    default: NangoCtor;
  };
  const Nango: NangoCtor = mod.default;
  const nango = new Nango();

  let settled = false;
  const connect = nango.openConnectUI({
    onEvent: (event) => {
      if (event.type === 'connect') {
        settled = true;
        const connectionId =
          typeof event.payload?.connectionId === 'string'
            ? (event.payload.connectionId as string)
            : undefined;
        onResult({ connectionId });
      } else if (event.type === 'close') {
        if (!settled) onClose?.();
      }
    },
  });

  connect.setSessionToken(sessionToken);
}
